import { useState, ReactNode } from 'react'
import { Box, styled } from '@mui/material'
import { Typography, Button, Tag } from '@carto/meridian-ds/components'
import { NOTIFICATION_DURATION_IN_MS } from '@carto/meridian-ds/theme'
import { Dialog, DialogContent } from '@carto/meridian-ds/components'
import { FormattedMessage } from 'react-intl'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import OnboardingChecklistVideoDialog from './OnboardingChecklistVideoDialog'
import { Video } from '../types'
import { BaseDialogProps } from 'features/dialogs/types'
import useOnboardingChecklist, { OnboardingAction } from '../utils/useOnboardingChecklist'
import useMobileBuilder from 'features/builder/ui/useMobileBuilder'
import { useAIAgentCompleteTasksMutation } from 'features/genAi-onboarding/hooks/genAiOnboardingQueries'
import { useAppDispatch } from 'app/state'
import { AlertSeverity, Notification, showNotification } from 'features/notifications/state/notificationSlice'

import intl from 'app/localization/intl'

const ONBOARDING_ASSETS_URL = 'https://app-gallery.cartocdn.com/onboarding_assets'

const INITIAL_VIDEO_WORKSPACE = `${ONBOARDING_ASSETS_URL}/initial-video-workspace.webp`
const INITIAL_VIDEO_WORKSPACE_HOVER = `${ONBOARDING_ASSETS_URL}/initial-video-workspace-hover.webp`
const INITIAL_VIDEO_BUILDER = `${ONBOARDING_ASSETS_URL}/initial-video-builder.webp`
const INITIAL_VIDEO_BUILDER_HOVER = `${ONBOARDING_ASSETS_URL}/initial-video-builder-hover.webp`
const INITIAL_VIDEO_WORKFLOWS = `${ONBOARDING_ASSETS_URL}/initial-video-workflows.webp`
const INITIAL_VIDEO_WORKFLOWS_HOVER = `${ONBOARDING_ASSETS_URL}/initial-video-workflows-hover.webp`
const INITIAL_VIDEO_AI_AGENT = `${ONBOARDING_ASSETS_URL}/initial-video-ai-agent.webp`
const INITIAL_VIDEO_AI_AGENT_HOVER = `${ONBOARDING_ASSETS_URL}/initial-video-ai-agent-hover.webp`

export interface OnboardingInitialDialogProps extends Omit<BaseDialogProps, 'onClose'> {
  type: Video
  open: boolean
  onWatchVideo: () => void
  onSkip: () => void
  onClose?: () => void
  onCloseVideo?: VoidFunction
}

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(5, 3.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0),
  '& .paragraph-gap': {
    display: 'block',
    marginBottom: theme.spacing(1.25)
  },
  '& strong': {
    color: theme.palette.text.primary // or '#000000' for pure black
  }
}))

const RightPanel = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer'
}))

const StyledSVGContainer = styled(Box)(() => ({
  minWidth: '600px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}))

const StyledButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(5)
}))

const videoIdMap = {
  [Video.WORKSPACE]: OnboardingAction.WatchWorkspaceVideo,
  [Video.BUILDER]: OnboardingAction.WatchBuilderVideo,
  [Video.WORKFLOWS]: OnboardingAction.WatchWorkflowsVideo,
  [Video.AIAGENT]: OnboardingAction.WatchAIAgentVideo
}

const contentByType: Record<
  Video,
  {
    title: ReactNode
    description: ReactNode
    Image: React.ComponentType
    ImageHover: React.ComponentType
  }
> = {
  [Video.WORKSPACE]: {
    title: <FormattedMessage id="home.onboarding.onboardingVideo.workspaceTitle" />,
    description: (
      <FormattedMessage
        id="home.onboarding.onboardingVideo.description.workspace"
        values={{
          gap: <span className="paragraph-gap" />,
          strong: (content) => <strong>{content}</strong>
        }}
      />
    ),
    Image: () => <img src={INITIAL_VIDEO_WORKSPACE} height="560px" alt="" />,
    ImageHover: () => <img src={INITIAL_VIDEO_WORKSPACE_HOVER} height="560px" alt="" />
  },
  [Video.BUILDER]: {
    title: <FormattedMessage id="home.onboarding.onboardingVideo.builderTitle" />,
    description: (
      <FormattedMessage
        id="home.onboarding.onboardingVideo.description.builder"
        values={{
          gap: <span className="paragraph-gap" />,
          strong: (content) => <strong>{content}</strong>
        }}
      />
    ),
    Image: () => <img src={INITIAL_VIDEO_BUILDER} height="560px" alt="" />,
    ImageHover: () => <img src={INITIAL_VIDEO_BUILDER_HOVER} height="560px" alt="" />
  },
  [Video.WORKFLOWS]: {
    title: <FormattedMessage id="home.onboarding.onboardingVideo.workflowsTitle" />,
    description: (
      <FormattedMessage
        id="home.onboarding.onboardingVideo.description.workflows"
        values={{
          gap: <span className="paragraph-gap" />,
          strong: (content) => <strong>{content}</strong>
        }}
      />
    ),
    Image: () => <img src={INITIAL_VIDEO_WORKFLOWS} height="560px" alt="" />,
    ImageHover: () => <img src={INITIAL_VIDEO_WORKFLOWS_HOVER} height="560px" alt="" />
  },
  [Video.AIAGENT]: {
    title: <FormattedMessage id="home.onboarding.onboardingVideo.aiAgentTitle" />,
    description: (
      <FormattedMessage
        id="home.onboarding.onboardingVideo.description.aiAgent"
        values={{
          gap: <span className="paragraph-gap" />,
          strong: (content) => <strong>{content}</strong>
        }}
      />
    ),
    Image: () => <img src={INITIAL_VIDEO_AI_AGENT} height="560px" alt="" />,
    ImageHover: () => <img src={INITIAL_VIDEO_AI_AGENT_HOVER} height="560px" alt="" />
  }
}

const taskCompleteErrorNotification = (error: string): Notification => ({
  msg: error,
  formatted: false,
  type: AlertSeverity.Error,
  timeout: NOTIFICATION_DURATION_IN_MS * 2,
  closeable: true
})

function OnboardingInitialDialog({
  type,
  open,
  onWatchVideo,
  onSkip,
  onClose,
  onCloseVideo
}: OnboardingInitialDialogProps) {
  const dispatch = useAppDispatch()
  const [hoveredType, setHoveredType] = useState<Video | null>(null)
  const [showVideoDialog, setShowVideoDialog] = useState(false)
  const { completeOnboardingChecklist } = useOnboardingChecklist()

  const onError = (error) => {
    const notification = taskCompleteErrorNotification(error.message)
    dispatch(showNotification(notification))
  }

  const { mutate: completeAIAgentTask } = useAIAgentCompleteTasksMutation(onError)
  const { title, description, Image, ImageHover } = contentByType[type]
  const isHovered = type === hoveredType
  const isMobile = useMobileBuilder()

  const onClickWatchVideo = () => {
    setShowVideoDialog(true)
    onWatchVideo()
  }

  const handleSkip = () => {
    if (type === Video.AIAGENT) {
      completeAIAgentTask([OnboardingAction.WatchAIAgentVideo])
    } else {
      completeOnboardingChecklist(videoIdMap[type])
    }
    onSkip()
  }

  const handleCloseVideo = () => {
    if (type === Video.AIAGENT) {
      completeAIAgentTask([OnboardingAction.WatchAIAgentVideo])
    } else {
      completeOnboardingChecklist(videoIdMap[type])
    }
    setShowVideoDialog(false)
    onCloseVideo?.()
  }

  return (
    <>
      <Dialog size="xlarge" open={open} onClose={onClose} height="560px">
        <DialogContent withGutter={false}>
          <Box display="flex">
            <LeftPanel>
              {type === Video.AIAGENT && (
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  display="flex"
                  alignItems="center"
                  sx={{ pb: 1.25 }}
                >
                  <Tag
                    color="secondary"
                    label={intl.formatMessage({ id: 'common.menu.preview' })}
                    type="code"
                    variant="filled"
                  />
                </Typography>
              )}
              <Typography color="textPrimary" variant="h4" sx={{ pb: 3 }}>
                {title}
              </Typography>
              <Typography color="textSecondary" variant="body2" component="div">
                {description}
              </Typography>
              <StyledButtonsContainer>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onClickWatchVideo}
                  startIcon={<PlayCircleOutlineIcon />}
                >
                  <FormattedMessage id="home.onboarding.onboardingVideo.watchVideoButton" />
                </Button>
                <Button variant="text" color="primary" onClick={handleSkip}>
                  <FormattedMessage id="home.onboarding.onboardingVideo.skipButton" />
                </Button>
              </StyledButtonsContainer>
            </LeftPanel>

            {!isMobile && (
              <RightPanel
                onMouseEnter={() => setHoveredType(type)}
                onMouseLeave={() => setHoveredType(null)}
                onClick={onClickWatchVideo}
              >
                <StyledSVGContainer>{isHovered ? <ImageHover /> : <Image />}</StyledSVGContainer>
              </RightPanel>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <OnboardingChecklistVideoDialog video={type} open={showVideoDialog} onClose={handleCloseVideo} />
    </>
  )
}

export default OnboardingInitialDialog
